import { Link } from "react-router-dom";
import { useAdminBidStats, useAdminRecentBids } from "@/hooks/use-queries";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Gavel, Clock3, CheckCircle2, XCircle, TimerReset, Briefcase, Bot, User } from "lucide-react";

const BID_STATUS_COLORS: Record<string, string> = {
  PENDING: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  ACCEPTED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  REJECTED: "bg-red-500/10 text-red-400 border-red-500/20",
  WITHDRAWN: "bg-muted text-muted-foreground border-divider",
  EXPIRED: "bg-muted text-muted-foreground border-divider",
};

function StatCard({
  icon: Icon,
  iconColor,
  label,
  value,
  sub,
}: {
  icon: React.ElementType;
  iconColor: string;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card className="glass-panel">
      <CardContent className="p-5">
        <div className="mb-3 flex items-start justify-between">
          <div className={`rounded-lg bg-surface-subtle p-2 ${iconColor}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <p className="mb-1 text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-2xl font-display font-bold">{value}</p>
        {sub && <p className="mt-0.5 text-[10px] font-mono text-muted-foreground">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function RecentBids() {
  const { data: stats, isLoading: statsLoading } = useAdminBidStats();
  const { data: bids, isLoading: bidsLoading } = useAdminRecentBids(100);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-1 text-sm font-mono uppercase tracking-[0.2em] text-primary">Admin</h2>
        <h1 className="text-3xl font-display font-bold">Recent Bids</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Monitor the latest bid activity across jobs, agents, and bidders.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        {statsLoading ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl bg-surface-subtle" />)
        ) : (
          <>
            <StatCard icon={Gavel} iconColor="text-blue-400" label="Total Bids" value={stats?.total ?? "—"} />
            <StatCard icon={Clock3} iconColor="text-amber-400" label="Pending" value={stats?.pending ?? "—"} />
            <StatCard icon={CheckCircle2} iconColor="text-emerald-400" label="Accepted" value={stats?.accepted ?? "—"} />
            <StatCard icon={XCircle} iconColor="text-red-400" label="Rejected" value={stats?.rejected ?? "—"} />
            <StatCard icon={TimerReset} iconColor="text-cyan-400" label="Last 7 Days" value={stats?.recent ?? "—"} />
          </>
        )}
      </div>

      <div className="space-y-3">
        <h3 className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
          <Gavel className="h-3.5 w-3.5" /> Latest 100 Bids
        </h3>
        <Card className="glass-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="border-b border-divider">
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Placed</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Job</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Poster</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Agent</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Bidder</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Amount</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Bid Status</th>
                  <th className="p-3 text-left font-normal uppercase tracking-wider text-muted-foreground">Credit</th>
                </tr>
              </thead>
              <tbody>
                {bidsLoading ? (
                  Array.from({ length: 12 }).map((_, i) => (
                    <tr key={i} className="border-b border-divider/60">
                      {Array.from({ length: 8 }).map((__, j) => (
                        <td key={j} className="p-3"><Skeleton className="h-4 w-full bg-surface-subtle" /></td>
                      ))}
                    </tr>
                  ))
                ) : !bids?.length ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      <div className="rounded-lg border-2 border-dashed border-divider py-8">
                        No bids found.
                      </div>
                    </td>
                  </tr>
                ) : (
                  bids.map((bid: any) => (
                    <tr key={bid.id} className="border-b border-divider/60 align-top transition-colors hover:bg-surface-subtle">
                      <td className="whitespace-nowrap p-3 text-muted-foreground">
                        <div>{new Date(bid.createdAt).toLocaleDateString()}</div>
                        <div className="text-[10px]">{new Date(bid.createdAt).toLocaleTimeString()}</div>
                      </td>
                      <td className="p-3">
                        <div className="flex min-w-[220px] items-start gap-2">
                          <Briefcase className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/70" />
                          <div className="min-w-0">
                            <Link to={`/jobs/${bid.job.id}`} className="block truncate font-medium text-primary hover:underline">
                              {bid.job.title}
                            </Link>
                            <div className="mt-1">
                              <Badge variant="outline" className="text-[9px] py-0 bg-surface-subtle border-divider">
                                {bid.job.status}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex min-w-[170px] items-start gap-2">
                          <User className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <div className="min-w-0">
                            <Link to={`/admin/users`} className="block truncate font-medium hover:text-primary">
                              {bid.job.poster?.displayName || "—"}
                            </Link>
                            <p className="truncate text-[10px] text-muted-foreground">{bid.job.poster?.email || "—"}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex min-w-[150px] items-start gap-2">
                          <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0 text-cyan-400" />
                          <div className="min-w-0">
                            <Link to={`/agents/${bid.agent.id}`} className="block truncate font-medium text-primary hover:underline">
                              {bid.agent.name}
                            </Link>
                            <p className="truncate text-[10px] text-muted-foreground">@{bid.agent.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="min-w-[170px]">
                          <p className="truncate font-medium">{bid.bidder?.displayName || "—"}</p>
                          <p className="truncate text-[10px] text-muted-foreground">{bid.bidder?.email || "—"}</p>
                        </div>
                      </td>
                      <td className="whitespace-nowrap p-3 font-semibold">{Number(bid.proposedUsdc).toFixed(2)} USDC</td>
                      <td className="p-3">
                        <Badge
                          variant="outline"
                          className={`text-[9px] py-0 ${BID_STATUS_COLORS[bid.status] ?? "bg-surface-subtle text-muted-foreground border-divider"}`}
                        >
                          {bid.status}
                        </Badge>
                      </td>
                      <td className="p-3">
                        <Badge
                          variant="outline"
                          className={`text-[9px] py-0 ${bid.usedFreeBid ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" : "bg-blue-500/10 text-blue-400 border-blue-500/20"}`}
                        >
                          {bid.usedFreeBid ? "FREE" : "PAID"}
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
